import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { environment } from '../../../../../environments/environment';
import { KpiAdminManagementService } from '../../../../services/kpiadminmanagement.service';
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { CustomDialogService } from '../../../../services/customdialog.service';
import { DialogService } from "../../../../services/dialog.service";
import { KpiAdminGFFormComponent } from "./kpiadmingf-form.component";
import { KpiAdminNewKpiFormComponent } from "./kpiadminnewkpi-form.component";
import { MatDialog } from "@angular/material/dialog";
import { DefaultValuesService } from "../../../../services/defaultvalues.service";
import { KpiAccountHistoryPopup } from "./kpiaccount-historydialog/kpiaccount-historydialog.component";

type DimensionKey = 'BreakDown2' | 'BreakDown1' | 'ProductHierarchy' | 'MovementType';

interface DimensionSlot {
    key: DimensionKey;
    orderKey: `${DimensionKey}Order`;
    heading: string;
    caption: string;
    enabled: boolean;
    order: number;
    dimensionName: string;
    items: Dimension[];
}

const SHARED_HEADER_FIELDS: (keyof KpiAdminI)[] = [
    'MersyId', 'KpiPlan', 'KpiName', 'KpiUnit', 'KpiDefinitionContent', 'KpiDefinitionTechnical',
    'KpiDescription', 'KpiFormula', 'KpiOwner', 'AvailabilityLevel_ACT', 'AvailabilityLevel_PLANNED',
    'AvailabilityLevel_EST', 'AvailabilityLevel_ORG', 'FormatFactor', 'NegativeDiffIsGood', 'ReportingType',
    'KpiLevel', 'ReportingAggregate', 'HideKpi', 'isForecastRequired', 'NoOplRequired', 'NoActRequired',
    'IsAccumulative', 'FullName'
];

const GLOBAL_EXCELLENCE_LEVELS = new Set(['FIELDBRANCH', 'MANAGEMENT']);

function copyField<K extends keyof KpiAdminI>(target: KpiAdminI, source: KpiAdminI, field: K) {
    target[field] = source[field];
}

function uniqueDimensionNames(list: Dimension[]): string[] {
    return [...new Set(list.map(item => item.DimensionName))];
}

function createSlot(key: DimensionKey, heading: string, caption = ''): DimensionSlot {
    return { key, orderKey: `${key}Order`, heading, caption, enabled: false, order: 0, dimensionName: '', items: [] };
}

@Component({
    selector: "kpiadmin",
    templateUrl: './kpiadmin.component.html',
    styleUrls: ['../../../../style/azdatafactory.component.less',
        '../../../../style/custom-mat-dialog.component.less',
        '../../../../style/container-tab-list.component.less',
        './kpiadmin.component.less',
        '../../../../style/new-generic-styles.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class KpiAdminComponent implements OnInit {

    public isProductionEnvironment = environment.production;

    public CurrentKpiIndex: number | null = null;
    public CurrentKpi: KpiAdminI;
    public CurrentKpiList: KpiAdminI[] = [];
    public AllKpiList: KpiAdminI[] = [];

    public isKpiListLoading = true;
    public isSaving = false;
    public isAddingGroupFunctions = false;

    BreakDownList: Dimension[] = [];
    ProductList: Dimension[] = [];
    MovementList: Dimension[] = [];

    dimensionSlots: DimensionSlot[] = [
        createSlot('BreakDown2', 'Dimension 2', 'Previous Breakdown2'),
        createSlot('BreakDown1', 'Dimension 1', 'Previous Breakdown1'),
        createSlot('ProductHierarchy', 'Dimension 3'),
        createSlot('MovementType', 'Dimension 4')
    ];

    dimensionNameOptions: Record<DimensionKey, string[]> = {
        BreakDown2: [],
        BreakDown1: [],
        ProductHierarchy: [],
        MovementType: []
    };

    GOTOKpi: string = "";

    KpiUnits: Reference[] = [];
    GroupFunctions: Reference[] = [];
    KpiOwners: Reference[] = [];
    ReportingTypes: Reference[] = [];
    KpiLevels: Reference[] = [];
    FormatFactors: Reference[] = [];

    calcFormulaDescription: string = '';
    calcFormulaError: number = 0;

    KpiForm: FormGroup = new FormGroup({});

    constructor(
        public defaults: DefaultValuesService,
        private kpiAdminService: KpiAdminManagementService,
        private formBuilder: FormBuilder,
        private customDialog: CustomDialogService
        , private dialog: DialogService
        , private dialogForm: MatDialog
    ) {
        this.CurrentKpi =
        {
            KpiDefinitionId: 0,
            MersyId: "",
            GroupFunction: "",
            KpiPlan: "",
            KpiName: "",
            KpiUnit: "",
            KpiDefinitionContent: "",
            KpiDefinitionTechnical: "",
            KpiDescription: "",
            KpiFormula: "",
            KpiOwner: "",
            AvailabilityLevel_ACT: "",
            AvailabilityLevel_PLANNED: "",
            AvailabilityLevel_EST: "",
            AvailabilityLevel_ORG: "",
            AvailabilityTime: "",
            FormatFactor: 0,
            NegativeDiffIsGood: false,
            ReportingType: "",
            KpiLevel: "",
            ReportingAggregate: "",
            BreakDown1: [],
            BreakDown1Order: 0,
            BreakDown2: [],
            BreakDown2Order: 0,
            MovementType: [],
            MovementTypeOrder: 0,
            ProductHierarchy: [],
            ProductHierarchyOrder: 0,
            HideKpi: false,
            isForecastRequired: false,
            FullName: "",
            GroupFunctionSubItems: [],
            Visible: true,
            Action: "",
            NoOplRequired: false,
            NoActRequired: false,
            IsAccumulative: false,
            IsVirtual: false,
            CalculationFormula: "",
            IsBranchExcellenceRelated: false,
            BranchExcellenceOrder: 0
        };
        this.KpiForm = this.formBuilder.group({
            KpiDefinitionId: [""],
            MersyId: [""],
            GroupFunction: [""],
            KpiPlan: [""],
            KpiName: [""],
            KpiUnit: [""],
            KpiDefinitionContent: [""],
            KpiDefinitionTechnical: [""],
            KpiDescription: [""],
            KpiFormula: [""],
            KpiOwner: [""],
            AvailabilityLevel_ACT: [""],
            AvailabilityLevel_PLANNED: [""],
            AvailabilityLevel_EST: [""],
            AvailabilityLevel_ORG: [""],
            AvailabilityTime: [""],
            FormatFactor: [""],
            NegativeDiffIsGood: [""],
            ReportingType: [""],
            KpiLevel: [""],
            ReportingAggregate: [""],
            BreakDown1: [""],
            BreakDown1Order: [""],
            BreakDown2: [""],
            BreakDown2Order: [""],
            MovementType: [""],
            MovementTypeOrder: [""],
            ProductHierarchy: [""],
            ProductHierarchyOrder: [""],
            HideKpi: [false],
            isForecastRequired: [false],
            FullName: "",
            GroupFunctionSubItems: [],
            Visible: true,
            Action: "",
            NoOplRequired: false,
            NoActRequired: false,
            IsAccumulative: false,
            IsVirtual: false,
            CalculationFormula: "",
            IsBranchExcellenceRelated: false,
            BranchExcellenceOrder: 0
        });
    }

    get canEdit(): boolean {
        return this.defaults.isMersyAdminUser;
    }

    get hasPendingChanges(): boolean {
        return this.KpiForm.dirty;
    }

    get isLocked(): boolean {
        return this.hasPendingChanges || this.isSaving || this.isAddingGroupFunctions;
    }

    get isVirtual(): boolean {
        return this.KpiForm.get('IsVirtual')?.value === true;
    }

    get isGlobalExcellenceLevel(): boolean {
        return GLOBAL_EXCELLENCE_LEVELS.has(this.KpiForm.get('KpiLevel')?.value);
    }

    get isBranchExcellenceRelated(): boolean {
        return this.KpiForm.get('IsBranchExcellenceRelated')?.value === true;
    }

    get activeDimensionSlots(): DimensionSlot[] {
        return this.dimensionSlots.filter(slot => slot.enabled);
    }

    get dimensionPositions(): number[] {
        return this.activeDimensionSlots.map((_, index) => index + 1);
    }

    get dimensionError(): string {
        const active = this.activeDimensionSlots;
        const empty = active.find(slot => !this.hasDimensionValues(slot));
        if (empty) {
            return `${empty.heading} is enabled but has no values selected.`;
        }
        const orders = active.map(slot => Number(slot.order)).sort((a, b) => a - b);
        return orders.every((order, index) => order === index + 1) ? '' : 'Kpi Dimension Order invalid';
    }

    get saveBlockedReason(): string {
        if (this.calcFormulaError !== 0) {
            return 'Fix the calculation formula before saving.';
        }
        return this.dimensionError;
    }

    private hasDimensionValues(slot: DimensionSlot): boolean {
        const values = this.KpiForm.get(slot.key)?.value;
        return Array.isArray(values) && values.length > 0;
    }

    dimensionControl(key: DimensionKey): FormControl {
        return this.KpiForm.get(key) as FormControl;
    }

    ngOnInit(): void {
        this.KpiForm.disable({ emitEvent: false });

        this.refreshFullList();

        this.kpiAdminService.getKpiDimension('MovementType', (r: Dimension[]) => {
            this.MovementList = r;
            this.onDimensionListLoaded();
        });

        this.kpiAdminService.getKpiDimension('ProductHierarchy', (r: Dimension[]) => {
            this.ProductList = r;
            this.onDimensionListLoaded();
        });

        this.kpiAdminService.getKpiDimension('BreakDown', (r: Dimension[]) => {
            this.BreakDownList = r;
            this.onDimensionListLoaded();
        });

        this.kpiAdminService.getKpiDefinitionReferences((r: Reference[]) => {
            this.KpiUnits = r.filter(x => x.Key === "KpiUnit");
            this.GroupFunctions = r.filter(x => x.Key === "GroupFunction");
            this.KpiOwners = r.filter(x => x.Key === "KpiOwner");
            this.ReportingTypes = r.filter(x => x.Key === "ReportingType");
            this.KpiLevels = r.filter(x => x.Key === "KpiLevel");
            this.FormatFactors = r.filter(x => x.Key === "FormatFactor");
        });

        this.KpiForm.get('IsVirtual')?.valueChanges.subscribe(() => this.updateCalcFormulaDescription());
    }

    undoChanges() {
        this.refresh();
    }

    save() {
        const blockedReason = this.saveBlockedReason;
        if (blockedReason) {
            this.customDialog.openError({ message: blockedReason, caller: this });
            return;
        }

        if (!this.KpiForm.dirty) {
            return;
        }

        const payload: KpiAdminI = { ...this.KpiForm.value };
        this.dimensionSlots.forEach(slot => payload[slot.orderKey] = slot.order);

        this.isSaving = true;
        this.applyControlStates();
        this.kpiAdminService.updateKpiDefinition(payload, () => {
            this.isSaving = false;
            this.applySavedKpi(payload);
            this.refresh();
            this.dialog.doBack = false;
            this.dialog.showSuccessDialog('Kpi definition successfully updated (Internal code ' + payload.KpiDefinitionId + ')');
        }, () => {
            this.isSaving = false;
            this.applyControlStates();
            this.customDialog.openError({ message: 'Kpi definition could not be saved. Your changes are still in the form, please try again.', caller: this });
        });
    }

    private applySavedKpi(saved: KpiAdminI) {
        saved.FullName = saved.MersyId + " " + saved.KpiName;

        const itemIndex = this.AllKpiList.findIndex(item => item.GroupFunction === this.CurrentKpi.GroupFunction && item.MersyId === this.CurrentKpi.MersyId);
        if (itemIndex != -1) {
            this.AllKpiList[itemIndex] = { ...saved };
        }
        this.CurrentKpi = { ...saved };

        this.AllKpiList
            .filter(item => item.MersyId === saved.MersyId)
            .forEach(item => SHARED_HEADER_FIELDS.forEach(field => copyField(item, saved, field)));

        const listed = this.CurrentKpiList.find(item => item.MersyId === saved.MersyId);
        if (listed) {
            listed.FullName = saved.FullName;
        }
        this.CurrentKpiList = [...this.CurrentKpiList];
    }

    deleteGF() {
        const kpi = this.CurrentKpi;
        this.customDialog.openConfirm({
            title: "DELETE Group Function",
            message: 'Group Function "' + kpi.GroupFunction + '" will be DELETED for KPI "' + kpi.FullName + '". Continue?',
            caller: this
        });
        this.customDialog.confirmed().subscribe(res => {
            if (res?.confirmed !== true) {
                return;
            }
            this.kpiAdminService.deleteKpi(kpi, () => {
                const remaining = kpi.GroupFunctionSubItems.filter(groupFunction => groupFunction !== kpi.GroupFunction);
                this.AllKpiList = this.AllKpiList.filter(item => item.KpiDefinitionId !== kpi.KpiDefinitionId);

                const siblings = this.AllKpiList.filter(item => item.MersyId === kpi.MersyId);
                siblings.forEach(item => item.GroupFunctionSubItems = remaining);

                if (siblings.length > 0) {
                    this.CurrentKpi = siblings[0];
                    this.refresh();
                }
                else {
                    this.refreshFullList();
                }

                this.customDialog.openDisplayInfoDialog({
                    message: 'Group Function "' + kpi.GroupFunction + '" successfully DELETED from KPI "' + kpi.FullName + '"',
                    caller: this
                });
            }, error => {
                this.customDialog.openDisplayInfoDialog({
                    message: 'KPI Group Function not deleted. ' + error,
                    caller: this
                });
            });
        });
    }

    deleteKpiCascade() {
        this.customDialog.openConfirm({
            title: 'DELETE KPI Cascade',
            message: 'KPI "' + this.CurrentKpi.FullName + '" and ALL its related data will be PERMANENTLY DELETED. Continue?',
            caller: this
        });
        this.customDialog.confirmed().subscribe(res => {
            if (res?.confirmed === true) {
                this.kpiAdminService.deleteKpiCascade(this.CurrentKpi, () => {
                    this.customDialog.openDisplayInfoDialog({
                        message: 'KPI "' + this.CurrentKpi.FullName + '" successfully DELETED (cascade).',
                        caller: this
                    });
                    this.refreshFullList();
                }, error => {
                    this.customDialog.openDisplayInfoDialog({
                        message: 'Error deleting KPI cascade. ' + error,
                        caller: this
                    });
                });
            }
        });
    }

    cleanKpi() {
        this.customDialog.openConfirm({
            title: 'CLEAN KPI',
            message: 'All relational data for KPI "' + this.CurrentKpi.FullName + '" will be deleted. The KPI definition will be kept. Continue?',
            caller: this
        });
        this.customDialog.confirmed().subscribe(res => {
            if (res?.confirmed === true) {
                this.kpiAdminService.cleanKpi(this.CurrentKpi, () => {
                    this.customDialog.openDisplayInfoDialog({
                        message: 'KPI "' + this.CurrentKpi.FullName + '" successfully CLEANED.',
                        caller: this
                    });
                    this.refreshFullList();
                }, error => {
                    this.customDialog.openDisplayInfoDialog({
                        message: 'Error cleaning KPI. ' + error,
                        caller: this
                    });
                });
            }
        });
    }

    NewKpi() {
        const Dialog = this.dialogForm.open(KpiAdminNewKpiFormComponent, {
            panelClass: 'custom-mat-dialog_v2',
            width: '560px',
            maxWidth: '95vw',
            disableClose: false,
            data: {
                "MersyId": this.CurrentKpi.MersyId,
                "KpiName": this.CurrentKpi.KpiName,
                "KpiDefinitionId": this.CurrentKpi.KpiDefinitionId,
                "SelectedGF": this.CurrentKpi.GroupFunction
            }
        });
        Dialog.afterClosed().subscribe(result => {
            if (result) {
                this.dialog.showSuccessDialog(`KPI ${result.MersyId_NEW} ${result.KpiName_NEW} sucessfully added (${result.KpiAdded})`);
                this.GOTOKpi = result.MersyId_NEW;
                this.refreshFullList();
            }
        });
    }

    alterGF() {
        const Dialog = this.dialogForm.open(KpiAdminGFFormComponent, {
            panelClass: 'custom-mat-dialog_v2',
            width: '480px',
            maxWidth: '95vw',
            disableClose: false,
            data: {
                "GroupFunctions": this.GroupFunctions,
                "SelectedGF": this.CurrentKpi.GroupFunctionSubItems,
                "MersyId": this.CurrentKpi.MersyId,
                "KpiName": this.CurrentKpi.KpiName,
                "KpiDefinitionId": this.CurrentKpi.KpiDefinitionId
            }
        });
        Dialog.afterClosed().subscribe((result: string[] | false) => {
            if (result && result.length > 0) {
                this.addGroupFunctions(result);
            }
        });
    }

    private addGroupFunctions(groupFunctions: string[]) {
        const reference = { ...this.CurrentKpi };
        const added: KpiAdminI[] = [];
        const failed: string[] = [];
        let pending = groupFunctions.length;
        this.isAddingGroupFunctions = true;

        const onDone = () => {
            if (--pending > 0) {
                return;
            }
            this.isAddingGroupFunctions = false;
            const subItems = [...reference.GroupFunctionSubItems, ...added.map(kpi => kpi.GroupFunction)];
            this.AllKpiList
                .filter(item => item.MersyId === reference.MersyId)
                .forEach(item => item.GroupFunctionSubItems = subItems);

            const lastAdded = added.at(-1);
            if (lastAdded) {
                this.CurrentKpi = { ...lastAdded };
                this.refresh();
            }

            const messages: string[] = [];
            if (added.length > 0) {
                messages.push('Group Function(s) "' + added.map(kpi => kpi.GroupFunction).join(', ') + '" successfully added to KPI "' + reference.FullName + '".');
            }
            if (failed.length > 0) {
                messages.push('Group Function(s) "' + failed.join(', ') + '" could not be added.');
            }
            this.customDialog.openDisplayInfoDialog({ message: messages.join(' '), caller: this });
        };

        groupFunctions.forEach(groupFunction => {
            const param = {
                "KpiDefinitionId": reference.KpiDefinitionId,
                "GroupFunction": groupFunction
            };
            this.kpiAdminService.addGroupFunctionToKpi(param, kpiadded => {
                const kpi: KpiAdminI = {
                    ...reference,
                    KpiDefinitionId: Number.parseInt(kpiadded.toString()),
                    Action: "NEW",
                    GroupFunction: groupFunction
                };
                this.AllKpiList.push(kpi);
                added.push(kpi);
                onDone();
            }, () => {
                failed.push(groupFunction);
                onDone();
            });
        });
    }

    onKpiSelected() {
        const selected = this.CurrentKpiIndex === null ? undefined : this.CurrentKpiList[this.CurrentKpiIndex];
        const kpi = selected && this.AllKpiList.find(item => item.MersyId === selected.MersyId);
        if (kpi) {
            this.CurrentKpi = kpi;
            this.refresh();
        }
    }

    selectGroupFunction(groupFunction: string) {
        const kpi = this.AllKpiList.find(item => item.GroupFunction === groupFunction && item.MersyId === this.CurrentKpi.MersyId);
        if (kpi) {
            this.CurrentKpi = { ...kpi };
            this.refresh();
        }
    }

    onDimensionToggle(slot: DimensionSlot, enabled: boolean) {
        slot.enabled = enabled;
        slot.order = enabled ? this.dimensionSlots.length + 1 : 0;
        if (!enabled) {
            slot.dimensionName = "";
        }
        this.normalizeDimensionOrders();
        this.resetDimensionValues(slot);
    }

    onDimensionNameChange(slot: DimensionSlot, dimensionName: string) {
        slot.dimensionName = dimensionName;
        this.resetDimensionValues(slot);
    }

    onDimensionOrderChange(slot: DimensionSlot, order: number) {
        const swapWith = this.dimensionSlots.find(other => other !== slot && other.enabled && other.order === order);
        if (swapWith) {
            swapWith.order = slot.order;
        }
        slot.order = order;
        this.KpiForm.markAsDirty();
    }

    refresh() {
        this.KpiForm.reset(undefined, { emitEvent: false });
        this.KpiForm.patchValue(this.CurrentKpi, { emitEvent: false });
        this.deriveDimensionSlots();
        this.updateCalcFormulaDescription();
    }

    refreshFullList() {
        this.isKpiListLoading = true;
        this.kpiAdminService.getKpiDefinitionList((r: KpiAdminI[]) => {
            this.AllKpiList = r;
            this.CurrentKpiList = r.filter(item => item.Visible === true);
            this.CurrentKpiList.forEach((item, index) => item.index = index);

            const target = this.GOTOKpi !== "" ? this.AllKpiList.find(item => item.MersyId === this.GOTOKpi) : undefined;
            this.GOTOKpi = "";

            this.CurrentKpi = target ?? this.CurrentKpiList[0] ?? this.CurrentKpi;
            this.CurrentKpiIndex = this.CurrentKpiList.find(item => item.MersyId === this.CurrentKpi.MersyId)?.index ?? null;
            this.isKpiListLoading = false;
            this.refresh();
        }, () => {
            this.isKpiListLoading = false;
        });
    }

    checkKPIAccountHistory() {
        this.dialogForm.open(KpiAccountHistoryPopup, {
            height: '525px',
            width: '1500px',
            maxWidth: '95vw',
            data: { KpiDefinitionId: this.CurrentKpi.KpiDefinitionId }
        });
    }

    updateCalcFormulaDescription() {
        this.calcFormulaError = 0;

        if (this.isVirtual) {
            let calFormula = this.KpiForm.get('CalculationFormula')?.value;

            if (calFormula) {
                calFormula = this.formatCalculationFormula(calFormula);

                const operands = calFormula.match(/\d+/g) || [];

                const mappedOperands = operands.map(id => {
                    const kpi = this.CurrentKpiList.find(k => k.KpiDefinitionId == id);
                    return kpi ? kpi.MersyId : null;
                });

                if (mappedOperands.includes(null)) {
                    console.error('KPI not found for given KpiDefinitionId');
                    this.calcFormulaDescription = 'Invalid KPI Definition Ids';
                    this.calcFormulaError = -1;
                    return;
                }

                let updatedFormula = '';
                let operandIndex = 0;
                for (let i = 0; i < calFormula.length; i++) {
                    if (calFormula[i].match(/\d/)) {
                        updatedFormula += mappedOperands[operandIndex++];
                        while (i < calFormula.length && calFormula[i].match(/\d/)) {
                            i++;
                        }
                        i--;
                    } else {
                        updatedFormula += calFormula[i];
                    }
                }

                this.calcFormulaDescription = updatedFormula;
                this.KpiForm.get('CalculationFormula')?.setValue(calFormula, { emitEvent: false });
            } else {
                console.error('Calculation formula is empty');
                this.calcFormulaDescription = 'Calculation formula is empty';
                this.calcFormulaError = -1;
            }
        }
    }

    private onDimensionListLoaded() {
        this.dimensionSlots.forEach(slot => this.dimensionNameOptions[slot.key] = uniqueDimensionNames(this.dimensionSource(slot.key)));

        if (!this.hasPendingChanges) {
            this.deriveDimensionSlots();
            return;
        }
        this.dimensionSlots
            .filter(slot => slot.enabled && !slot.dimensionName)
            .forEach(slot => {
                slot.dimensionName = this.dimensionNameOf(slot.key, this.KpiForm.get(slot.key)?.value);
                this.updateSlotItems(slot);
            });
    }

    private dimensionNameOf(key: DimensionKey, values: unknown): string {
        const firstCode = Array.isArray(values) ? values[0] : undefined;
        return firstCode === undefined
            ? ""
            : this.dimensionSource(key).find(item => item.Code.toString() === String(firstCode))?.DimensionName ?? "";
    }

    private dimensionSource(key: DimensionKey): Dimension[] {
        switch (key) {
            case 'MovementType':
                return this.MovementList;
            case 'ProductHierarchy':
                return this.ProductList;
            default:
                return this.BreakDownList;
        }
    }

    private deriveDimensionSlots() {
        this.dimensionSlots.forEach(slot => {
            const values = this.CurrentKpi[slot.key] ?? [];
            slot.enabled = values.length > 0;
            slot.order = slot.enabled ? this.CurrentKpi[slot.orderKey] : 0;
            slot.dimensionName = slot.enabled ? this.dimensionNameOf(slot.key, values) : "";
            this.updateSlotItems(slot);
        });
        this.applyControlStates();
    }

    private updateSlotItems(slot: DimensionSlot) {
        slot.items = slot.enabled
            ? this.dimensionSource(slot.key).filter(item => item.DimensionName === slot.dimensionName)
            : [];
    }

    private normalizeDimensionOrders() {
        this.activeDimensionSlots
            .sort((a, b) => a.order - b.order)
            .forEach((slot, index) => slot.order = index + 1);
    }

    private resetDimensionValues(slot: DimensionSlot) {
        this.KpiForm.get(slot.key)?.reset();
        this.updateSlotItems(slot);
        this.applyControlStates();
        this.KpiForm.markAsDirty();
    }

    private applyControlStates() {
        if (!this.canEdit || !this.CurrentKpi.KpiDefinitionId || this.isSaving) {
            this.KpiForm.disable({ emitEvent: false });
            return;
        }
        this.KpiForm.enable({ emitEvent: false });
        this.dimensionSlots
            .filter(slot => !slot.enabled)
            .forEach(slot => this.KpiForm.get(slot.key)?.disable({ emitEvent: false }));
    }

    private formatCalculationFormula(formula: string): string {
        const regex = /\d+|[+\-*/()]/g;
        const tokens = formula.match(regex);
        if (tokens) {
            return tokens.join(' ');
        } else {
            console.error('Invalid calculation formula format');
            return '';
        }
    }
}
